import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Modal, Text } from '@teals/ui-web';
import {
  useSendMessageMutation,
  useUpdateAgentConfigMutation,
  useUpdateAgentDataSourcesMutation,
  useGetDataSourcesQuery,
  type Agent,
  type AgentDataSourceRef,
  type AgentDetail,
  type DataSource,
} from '../../store/api';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import type { ExchangeEvent } from '../../hooks/useWebSocket';
import {
  ConfigureFleetPanel,
  ConfigureMembershipPanel,
  isManagementAgent,
} from './AgentSystemPanels';
import {
  DS_TYPE_LABELS,
  DS_TYPE_BADGE_VARIANT,
} from '../DataSources/constants';
import type { DsType } from '../../store/api/dataSourcesApi';

const BUILDER_AGENT = 'agent-builder';

const MODEL_OPTIONS = [
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4o',
  'gpt-4o-mini',
  'claude-sonnet-4',
  'claude-3-5-sonnet',
];

interface AgentConfig {
  systemPrompt: string;
  model: string;
  temperature: number;
  maxTokens: number;
  responseMode: string;
  recommendedModels: string[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface ProposedConfig {
  systemPrompt?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  responseMode?: string;
  recommendedModels?: string[];
  dataSources?: string[];
  fleet?: string[];
  agentType?: string;
  orchestrationMode?: string;
}

interface BuilderPayload {
  stage?: string;
  message?: string;
  suggestions?: string[];
  agentConfig?: Record<string, unknown>;
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 520px;

  @media (max-width: 1023px) {
    min-height: min(520px, 50dvh);
  }
`;

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 420px;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-surface);

  @media (max-width: 1023px) {
    height: min(420px, 55dvh);
  }
`;

const ChatHeader = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
`;

const ChatTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChatSubtitle = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 90%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) => ($role === 'user' ? 'white' : 'var(--text-primary)')};
  white-space: pre-wrap;
`;

const PendingBubble = styled.div`
  align-self: flex-start;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  background: var(--bg-elevated, rgba(148, 163, 184, 0.12));
  color: var(--text-secondary);
  font-size: 0.8125rem;
`;

const Dropzone = styled.div<{ $over?: boolean }>`
  border: 2px ${({ $over }) => ($over ? 'solid' : 'dashed')}
    ${({ $over }) => ($over ? 'var(--accent)' : 'var(--border)')};
  border-radius: 12px;
  padding: 0.5rem;
  margin: 0 0.75rem 0.75rem;
  background: ${({ $over }) =>
    $over
      ? 'color-mix(in srgb, var(--accent) 12%, var(--bg-surface))'
      : 'var(--bg-elevated, #f8fafc)'};
  transition: border-color 0.15s, background 0.15s;
`;

const DropLabel = styled.div<{ $over?: boolean }>`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ $over }) => ($over ? 'var(--accent)' : 'var(--text-muted)')};
  margin-bottom: 0.375rem;
`;

const AttachedRefs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.375rem;
`;

const AttachedRef = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: color-mix(in srgb, var(--accent) 12%, transparent);
  color: var(--accent);
  font-size: 0.6875rem;
  font-weight: 600;
`;

const ChatInputRow = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`;

const ChatInput = styled.textarea`
  flex: 1;
  min-height: 44px;
  max-height: 120px;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--text-primary);
  background: transparent;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: var(--accent);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Chip = styled.button`
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DiffCard = styled.div`
  padding: 0.875rem;
  border: 1px solid var(--warning, #d97706);
  border-radius: 8px;
  background: color-mix(in srgb, var(--warning, #d97706) 8%, transparent);
  font-size: 0.8125rem;
`;

const DiffActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const PreviewCard = styled(Card)`
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1023px) {
    position: static;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BlockHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 0.8125rem;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const FieldName = styled.span`
  color: var(--text-muted);
  flex-shrink: 0;
  padding-top: 0.35rem;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  align-items: flex-end;
`;

const ConfigInput = styled.input`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  font-family: inherit;
  max-width: 14rem;
  width: 100%;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfigSelect = styled.select`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  font-family: inherit;
  max-width: 14rem;
  width: 100%;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfigTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  font-size: 0.8125rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--text-primary);
  background: var(--bg-elevated, #f8fafc);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem;
  resize: vertical;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RemovableChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  line-height: 1;

  &:hover:not(:disabled) {
    color: var(--danger, #dc2626);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-of-type {
    border-bottom: none;
  }
`;

const DsDragTray = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 2px dashed color-mix(in srgb, var(--accent) 35%, var(--border));
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent) 6%, var(--bg-elevated, #f8fafc));
`;

const DsDragLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--accent);
  margin-bottom: 0.5rem;
`;

const DsChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-primary);
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Grip = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0.55;

  span {
    display: block;
    width: 10px;
    height: 2px;
    border-radius: 1px;
    background: var(--text-muted);
  }
`;

const ComingSoon = styled.div`
  opacity: 0.65;
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: 0.75rem;
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
`;

const DraftBanner = styled.div`
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--warning, #d97706);
  background: color-mix(in srgb, var(--warning, #d97706) 10%, transparent);
  font-size: 0.875rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const Helper = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 280px;
  overflow: auto;
  margin: 1rem 0;
`;

const PickerRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
`;

function parseBuilderResponse(raw: string): BuilderPayload {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { message: '' };

  const tryParse = (text: string): BuilderPayload | null => {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      return {
        stage: parsed.stage ? String(parsed.stage) : undefined,
        message: parsed.message
          ? String(parsed.message)
          : typeof parsed === 'object'
            ? undefined
            : text,
        suggestions: Array.isArray(parsed.suggestions)
          ? parsed.suggestions.map((s) => String(s))
          : undefined,
        agentConfig:
          parsed.agent_config && typeof parsed.agent_config === 'object'
            ? (parsed.agent_config as Record<string, unknown>)
            : parsed.agentConfig && typeof parsed.agentConfig === 'object'
              ? (parsed.agentConfig as Record<string, unknown>)
              : undefined,
      };
    } catch {
      return null;
    }
  };

  const direct = tryParse(trimmed);
  if (direct) {
    return {
      ...direct,
      message: direct.message || direct.agentConfig ? direct.message : trimmed,
    };
  }

  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence?.[1]) {
    const fromFence = tryParse(fence[1].trim());
    if (fromFence) {
      return {
        ...fromFence,
        message:
          fromFence.message ||
          trimmed.replace(fence[0], '').trim() ||
          fromFence.message,
      };
    }
  }

  const brace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (brace >= 0 && lastBrace > brace) {
    const fromSlice = tryParse(trimmed.slice(brace, lastBrace + 1));
    if (fromSlice) {
      return {
        ...fromSlice,
        message:
          fromSlice.message ||
          trimmed.slice(0, brace).trim() ||
          fromSlice.message,
      };
    }
  }

  return { message: trimmed };
}

function mapDataSourceNames(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        const o = entry as Record<string, unknown>;
        return String(o.name ?? o.data_source_id ?? o.dataSourceId ?? '');
      }
      return '';
    })
    .filter(Boolean);
}

function toProposed(agentConfig: Record<string, unknown>): ProposedConfig {
  const settings =
    agentConfig.settings && typeof agentConfig.settings === 'object'
      ? (agentConfig.settings as Record<string, unknown>)
      : {};
  const fleet =
    agentConfig.fleet && typeof agentConfig.fleet === 'object'
      ? (agentConfig.fleet as Record<string, unknown>)
      : {};
  const orch =
    agentConfig.orchestration && typeof agentConfig.orchestration === 'object'
      ? (agentConfig.orchestration as Record<string, unknown>)
      : {};
  const fleetAgents = Array.isArray(fleet.agents)
    ? fleet.agents.map((a) => String(a))
    : undefined;
  const recommended =
    agentConfig.recommended_models ??
    agentConfig.recommendedModels ??
    settings.recommended_models;
  const dataSources =
    mapDataSourceNames(agentConfig.data_sources) ??
    mapDataSourceNames(agentConfig.dataSources);

  return {
    systemPrompt: agentConfig.system_prompt
      ? String(agentConfig.system_prompt)
      : agentConfig.systemPrompt
        ? String(agentConfig.systemPrompt)
        : undefined,
    model: agentConfig.llm_model
      ? String(agentConfig.llm_model)
      : agentConfig.model
        ? String(agentConfig.model)
        : undefined,
    temperature:
      typeof settings.temperature === 'number'
        ? settings.temperature
        : typeof agentConfig.temperature === 'number'
          ? agentConfig.temperature
          : undefined,
    maxTokens:
      typeof settings.max_tokens === 'number'
        ? settings.max_tokens
        : typeof agentConfig.max_tokens === 'number'
          ? agentConfig.max_tokens
          : typeof agentConfig.maxTokens === 'number'
            ? agentConfig.maxTokens
            : undefined,
    responseMode: settings.response_mode
      ? String(settings.response_mode)
      : agentConfig.response_mode
        ? String(agentConfig.response_mode)
        : agentConfig.responseMode
          ? String(agentConfig.responseMode)
          : undefined,
    recommendedModels: Array.isArray(recommended)
      ? recommended.map((m) => String(m)).filter(Boolean)
      : undefined,
    dataSources,
    fleet: fleetAgents,
    agentType: agentConfig.agent_type
      ? String(agentConfig.agent_type)
      : agentConfig.agentType
        ? String(agentConfig.agentType)
        : undefined,
    orchestrationMode: orch.mode ? String(orch.mode) : undefined,
  };
}

function configsDiffer(
  current: AgentConfig,
  fleetNames: string[],
  dataSourceNames: string[],
  proposed: ProposedConfig,
): boolean {
  if (
    proposed.systemPrompt != null &&
    proposed.systemPrompt !== current.systemPrompt
  ) {
    return true;
  }
  if (proposed.model != null && proposed.model !== current.model) return true;
  if (
    proposed.temperature != null &&
    proposed.temperature !== current.temperature
  ) {
    return true;
  }
  if (proposed.maxTokens != null && proposed.maxTokens !== current.maxTokens) {
    return true;
  }
  if (
    proposed.responseMode != null &&
    proposed.responseMode !== current.responseMode
  ) {
    return true;
  }
  if (proposed.recommendedModels) {
    const a = [...proposed.recommendedModels].sort().join(',');
    const b = [...current.recommendedModels].sort().join(',');
    if (a !== b) return true;
  }
  if (proposed.dataSources) {
    const a = [...proposed.dataSources].sort().join(',');
    const b = [...dataSourceNames].sort().join(',');
    if (a !== b) return true;
  }
  if (proposed.fleet) {
    const a = [...proposed.fleet].sort().join(',');
    const b = [...fleetNames].sort().join(',');
    if (a !== b) return true;
  }
  return false;
}

function buildConfigFromAgent(agent: AgentDetail): AgentConfig {
  return {
    systemPrompt: agent.config?.systemPrompt || '',
    model: agent.config?.model || agent.llmModel || 'gpt-4o',
    temperature: agent.config?.temperature ?? 0.7,
    maxTokens: agent.config?.maxTokens ?? 2000,
    responseMode: agent.config?.responseMode || 'sync',
    recommendedModels:
      agent.recommendedModels ||
      agent.config?.recommendedModels ||
      [],
  };
}

function resolveAssociatedSources(
  agent: AgentDetail,
  catalog: DataSource[] | undefined,
): AgentDataSourceRef[] {
  if (agent.dataSources?.length) return agent.dataSources;
  if (!catalog?.length) return [];
  return catalog
    .filter(
      (ds) =>
        ds.agentIds.includes(agent.id) ||
        ds.agentIds.includes(agent.agentName),
    )
    .map((ds) => ({
      dataSourceId: ds.dataSourceId,
      name: ds.name,
      dsType: ds.dsType,
      status: ds.status,
    }));
}

function dsTypeLabel(type?: string): string {
  if (!type) return 'Data source';
  return DS_TYPE_LABELS[type as DsType] || type;
}

function greetingFor(
  agent: AgentDetail,
  config: AgentConfig,
  dsCount: number,
  fleetCount: number,
  readOnly: boolean,
  needsVersion: boolean,
): string {
  if (readOnly) {
    return `I'm the configuration assistant for ${agent.name}. This agent is read-only — I can explain settings but cannot apply changes.`;
  }
  const typeLabel = isManagementAgent(agent) ? 'Management' : 'Agent';
  const status = needsVersion
    ? 'Published (create a version to edit)'
    : agent.hasOpenDraft
      ? `Draft ${agent.draftVersion || agent.version}`
      : agent.versionStatus;
  const fleetBit = isManagementAgent(agent)
    ? `, fleet of ${fleetCount}`
    : agent.membership
      ? `, part of ${agent.membership.parentAgentName}`
      : '';
  return (
    `I'm the builder for ${agent.name} (${typeLabel} · ${status}).\n\n` +
    `Current config: default ${config.model}, ${config.recommendedModels.length} recommended models, ` +
    `${dsCount} data source${dsCount === 1 ? '' : 's'}${fleetBit}.\n\n` +
    `Drag a data source chip into the input to reference it, or describe a change. ` +
    `I will never ask for API secrets.`
  );
}

export interface ConfigureTabProps {
  agent: AgentDetail;
  catalog: Agent[];
  readOnly: boolean;
  onCreateVersion?: () => void;
}

const ConfigureTab: React.FC<ConfigureTabProps> = ({
  agent,
  catalog,
  readOnly,
  onCreateVersion,
}) => {
  const navigate = useNavigate();
  const management = isManagementAgent(agent);
  const needsVersion =
    !readOnly &&
    !agent.hasOpenDraft &&
    agent.versionStatus === 'published';
  const editsEnabled = !readOnly && !needsVersion;

  const { data: orgDataSources } = useGetDataSourcesQuery();
  const associated = useMemo(
    () => resolveAssociatedSources(agent, orgDataSources),
    [agent, orgDataSources],
  );

  const [config, setConfig] = useState<AgentConfig>(() =>
    buildConfigFromAgent(agent),
  );
  const [savedConfig, setSavedConfig] = useState<AgentConfig>(() =>
    buildConfigFromAgent(agent),
  );
  const [fleetNames, setFleetNames] = useState<string[]>(
    agent.fleet?.agents?.map((m) => m.agentName) || [],
  );
  const [dataSources, setDataSources] =
    useState<AgentDataSourceRef[]>(associated);
  const [savedDataSourceIds, setSavedDataSourceIds] = useState<string[]>(
    associated.map((d) => d.dataSourceId),
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [attachedRefs, setAttachedRefs] = useState<string[]>([]);
  const [dropOver, setDropOver] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [pending, setPending] = useState<ProposedConfig | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [associateOpen, setAssociateOpen] = useState(false);
  const [pickerSelected, setPickerSelected] = useState<string[]>([]);
  const [removeConfirm, setRemoveConfirm] = useState<AgentDataSourceRef | null>(
    null,
  );
  const endRef = useRef<HTMLDivElement>(null);
  const greetedRef = useRef(false);

  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [updateConfig] = useUpdateAgentConfigMutation();
  const [updateAgentDataSources] = useUpdateAgentDataSourcesMutation();
  const {
    subscribe,
    unsubscribe,
    addListener,
    removeListener,
  } = useWebSocketContext();

  const showPending = isSending || awaitingReply;

  useEffect(() => {
    const next = buildConfigFromAgent(agent);
    setConfig(next);
    setSavedConfig(next);
    setFleetNames(agent.fleet?.agents?.map((m) => m.agentName) || []);
    const nextDs = resolveAssociatedSources(agent, orgDataSources);
    setDataSources(nextDs);
    setSavedDataSourceIds(nextDs.map((d) => d.dataSourceId));
    // Sync from agent / org catalog identity — avoid depending on full object refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agent.id,
    agent.config?.systemPrompt,
    agent.config?.model,
    agent.config?.temperature,
    agent.config?.maxTokens,
    agent.config?.responseMode,
    agent.llmModel,
    agent.hasOpenDraft,
    agent.versionStatus,
    JSON.stringify(agent.recommendedModels),
    JSON.stringify(agent.dataSources),
    JSON.stringify(agent.fleet?.agents?.map((m) => m.agentName)),
    JSON.stringify(orgDataSources?.map((d) => d.dataSourceId)),
  ]);

  useEffect(() => {
    if (greetedRef.current) return;
    greetedRef.current = true;
    const next = buildConfigFromAgent(agent);
    const ds = resolveAssociatedSources(agent, orgDataSources);
    setMessages([
      {
        id: 'greeting',
        role: 'assistant',
        content: greetingFor(
          agent,
          next,
          ds.length,
          agent.fleet?.agents?.length || 0,
          readOnly,
          needsVersion,
        ),
      },
    ]);
  }, [agent, orgDataSources, readOnly, needsVersion]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, awaitingReply]);

  useEffect(() => {
    if (!sessionId) return;
    subscribe(sessionId, BUILDER_AGENT);
    return () => unsubscribe();
  }, [sessionId, subscribe, unsubscribe]);

  const configRef = useRef(config);
  const fleetRef = useRef(fleetNames);
  const dsNamesRef = useRef(dataSources.map((d) => d.name));
  configRef.current = config;
  fleetRef.current = fleetNames;
  dsNamesRef.current = dataSources.map((d) => d.name);

  const dirty = useMemo(() => {
    const cfgDirty =
      config.systemPrompt !== savedConfig.systemPrompt ||
      config.model !== savedConfig.model ||
      config.temperature !== savedConfig.temperature ||
      config.maxTokens !== savedConfig.maxTokens ||
      config.responseMode !== savedConfig.responseMode ||
      [...config.recommendedModels].sort().join(',') !==
        [...savedConfig.recommendedModels].sort().join(',');
    const dsDirty =
      [...dataSources.map((d) => d.dataSourceId)].sort().join(',') !==
      [...savedDataSourceIds].sort().join(',');
    return cfgDirty || dsDirty;
  }, [config, savedConfig, dataSources, savedDataSourceIds]);

  const applyAssistantText = useCallback(
    (content: string) => {
      const parsed = parseBuilderResponse(content);
      const message =
        parsed.message ||
        (parsed.agentConfig
          ? 'Proposed configuration updates — review and Accept or Discard.'
          : content);
      setMessages((prev) => {
        if (prev.some((m) => m.content === message && m.role === 'assistant')) {
          return prev;
        }
        return [
          ...prev,
          { id: `a-${Date.now()}`, role: 'assistant', content: message },
        ];
      });
      if (parsed.suggestions?.length) {
        setSuggestions(parsed.suggestions);
      }
      if (parsed.agentConfig && editsEnabled) {
        const proposed = toProposed(parsed.agentConfig);
        if (
          configsDiffer(
            configRef.current,
            fleetRef.current,
            dsNamesRef.current,
            proposed,
          )
        ) {
          setPending(proposed);
        }
      }
    },
    [editsEnabled],
  );

  useEffect(() => {
    const listener = (event: ExchangeEvent) => {
      if (
        (event.type === 'exchange.response.completed' ||
          event.type === 'conversation.result' ||
          event.type === 'exchange.completed') &&
        event.data?.response
      ) {
        const content = String(event.data.response);
        setAwaitingReply(false);
        applyAssistantText(content);
      }
      if (
        event.type === 'conversation.result' &&
        (event.data?.status === 'failed' || event.data?.error)
      ) {
        setAwaitingReply(false);
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: String(
              event.data?.error ||
                event.data?.message ||
                'Configuration assistant failed.',
            ),
          },
        ]);
      }
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [addListener, removeListener, applyAssistantText]);

  const seedChips = useMemo(() => {
    if (readOnly || needsVersion) return [] as string[];
    const chips = [
      'Change default model',
      'Set recommended models',
      'Associate data source',
      'Create data source',
      'Update system prompt',
    ];
    if (management) {
      chips.unshift('Add fleet member');
      if (fleetNames.length) {
        chips.unshift(`Remove ${fleetNames[fleetNames.length - 1]} from fleet`);
      }
    }
    return chips;
  }, [readOnly, needsVersion, management, fleetNames]);

  const displayChips = suggestions.length ? suggestions : seedChips;

  const preview = useMemo(() => {
    return {
      systemPrompt: pending?.systemPrompt ?? config.systemPrompt,
      model: pending?.model ?? config.model,
      temperature: pending?.temperature ?? config.temperature,
      maxTokens: pending?.maxTokens ?? config.maxTokens,
      responseMode: pending?.responseMode ?? config.responseMode,
      recommendedModels:
        pending?.recommendedModels ?? config.recommendedModels,
      dataSources: pending?.dataSources ?? dataSources.map((d) => d.name),
      fleet: pending?.fleet ?? fleetNames,
      agentType: pending?.agentType ?? agent.agentType,
      orchestrationMode:
        pending?.orchestrationMode ?? agent.fleet?.orchestrationMode,
    };
  }, [pending, config, dataSources, fleetNames, agent.agentType, agent.fleet]);

  const promptContext = useMemo(
    () => ({
      mode: 'configure',
      edit_mode: true,
      read_only: readOnly || needsVersion,
      target_agent: {
        id: agent.id,
        agent_name: agent.agentName,
        name: agent.name,
        agent_type: agent.agentType,
      },
      current_config: {
        system_prompt: config.systemPrompt,
        llm_model: config.model,
        settings: {
          temperature: config.temperature,
          max_tokens: config.maxTokens,
          response_mode: config.responseMode,
        },
        recommended_models: config.recommendedModels,
        data_sources: dataSources.map((d) => ({
          data_source_id: d.dataSourceId,
          name: d.name,
        })),
        agent_type: agent.agentType,
        fleet: { agents: fleetNames },
        orchestration: {
          mode: agent.fleet?.orchestrationMode || 'fan_out',
        },
      },
      catalog_agents: catalog
        .filter((a) => a.id !== agent.id)
        .slice(0, 40)
        .map((a) => ({
          agent_name: a.agentName,
          name: a.name,
          agent_type: a.agentType,
        })),
    }),
    [
      agent,
      catalog,
      config,
      dataSources,
      fleetNames,
      readOnly,
      needsVersion,
    ],
  );

  const sendQuery = async (query: string, refs?: string[]) => {
    const attached = refs ?? attachedRefs;
    const text = query.trim();
    if ((!text && !attached.length) || showPending) return;

    const refPrefix =
      attached.length > 0
        ? attached.map((n) => `$DataSource:${n}`).join(' ') + '\n'
        : '';
    const fullQuery = `${refPrefix}${text}`.trim();

    setChatInput('');
    setAttachedRefs([]);
    setActionError(null);
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: fullQuery },
    ]);
    setAwaitingReply(true);
    try {
      const result = await sendMessage({
        query: fullQuery,
        agentName: BUILDER_AGENT,
        sessionId,
        metadata: {
          source: 'configure',
          prompt_context: promptContext,
          attached_data_sources: attached,
        },
      }).unwrap();
      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, BUILDER_AGENT);
      }
      const replyText = (result.response || '').trim();
      const pendingAsync =
        result.isAsync === true ||
        result.status === 'processing' ||
        (!replyText && Boolean(result.conversationId));
      if (pendingAsync) return;
      setAwaitingReply(false);
      if (replyText) applyAssistantText(replyText);
    } catch (err: unknown) {
      setAwaitingReply(false);
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      const message =
        status === 404
          ? 'Agent Builder is not available in this environment.'
          : 'Unable to reach the configuration assistant.';
      setActionError(message);
      setMessages((prev) => [
        ...prev,
        { id: `e-${Date.now()}`, role: 'assistant', content: message },
      ]);
    }
  };

  const handleChipClick = (chip: string) => {
    if (chip.toLowerCase().includes('create data source')) {
      navigate(
        `/data-sources?create=1&returnTo=${encodeURIComponent(`/agents/${agent.id}`)}&tab=configure`,
      );
      return;
    }
    if (chip.toLowerCase().includes('associate data source')) {
      setAssociateOpen(true);
      setPickerSelected(dataSources.map((d) => d.dataSourceId));
      return;
    }
    void sendQuery(chip);
  };

  const persistConfig = async (
    next: AgentConfig,
    nextFleet: string[],
    extra?: Record<string, unknown>,
  ) => {
    await updateConfig({
      id: agent.id,
      config: {
        system_prompt: next.systemPrompt,
        model: next.model,
        temperature: next.temperature,
        max_tokens: next.maxTokens,
        response_mode: next.responseMode,
        recommended_models: next.recommendedModels,
        ...(nextFleet.length || management
          ? {
              fleet: { agents: nextFleet },
              agent_type: 'management',
              orchestration: {
                mode:
                  (extra?.orchestrationMode as string) ||
                  agent.fleet?.orchestrationMode ||
                  'fan_out',
              },
            }
          : {}),
        ...extra,
      },
    }).unwrap();
  };

  const persistDataSources = async (ids: string[]) => {
    await updateAgentDataSources({
      id: agent.id,
      dataSourceIds: ids,
    }).unwrap();
  };

  const handleAccept = async () => {
    if (!pending || !editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      const next: AgentConfig = {
        systemPrompt: pending.systemPrompt ?? config.systemPrompt,
        model: pending.model ?? config.model,
        temperature: pending.temperature ?? config.temperature,
        maxTokens: pending.maxTokens ?? config.maxTokens,
        responseMode: pending.responseMode ?? config.responseMode,
        recommendedModels:
          pending.recommendedModels ?? config.recommendedModels,
      };
      const nextFleet = pending.fleet ?? fleetNames;
      await persistConfig(next, nextFleet, {
        orchestrationMode: pending.orchestrationMode,
      });

      let nextDs = dataSources;
      if (pending.dataSources) {
        const byName = new Map(
          (orgDataSources || []).map((d) => [d.name, d] as const),
        );
        const ids: string[] = [];
        const refs: AgentDataSourceRef[] = [];
        for (const name of pending.dataSources) {
          const existing = dataSources.find((d) => d.name === name);
          const fromCatalog = byName.get(name);
          if (existing) {
            ids.push(existing.dataSourceId);
            refs.push(existing);
          } else if (fromCatalog) {
            ids.push(fromCatalog.dataSourceId);
            refs.push({
              dataSourceId: fromCatalog.dataSourceId,
              name: fromCatalog.name,
              dsType: fromCatalog.dsType,
              status: fromCatalog.status,
            });
          }
        }
        await persistDataSources(ids);
        nextDs = refs;
        setSavedDataSourceIds(ids);
      }

      setConfig(next);
      setSavedConfig(next);
      setFleetNames(nextFleet);
      setDataSources(nextDs);
      setPending(null);
      setMessages((prev) => [
        ...prev,
        {
          id: `ok-${Date.now()}`,
          role: 'assistant',
          content:
            'Applied to Draft. Publish when you are ready to make this live.',
        },
      ]);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to apply proposal',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setPending(null);

  const handleFleetChange = async (names: string[]) => {
    setFleetNames(names);
    if (!editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      await persistConfig(config, names);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to update fleet',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardEdits = () => {
    setConfig(savedConfig);
    const restored = (orgDataSources || [])
      .filter((d) => savedDataSourceIds.includes(d.dataSourceId))
      .map((d) => ({
        dataSourceId: d.dataSourceId,
        name: d.name,
        dsType: d.dsType,
        status: d.status,
      }));
    if (restored.length > 0 || savedDataSourceIds.length === 0) {
      setDataSources(restored);
      return;
    }
    setDataSources(
      associated.filter((d) => savedDataSourceIds.includes(d.dataSourceId)),
    );
  };

  const handleSaveDraft = async () => {
    if (!editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      await persistConfig(config, fleetNames);
      const ids = dataSources.map((d) => d.dataSourceId);
      await persistDataSources(ids);
      setSavedConfig(config);
      setSavedDataSourceIds(ids);
      setMessages((prev) => [
        ...prev,
        {
          id: `save-${Date.now()}`,
          role: 'assistant',
          content: 'Saved Live Configuration to Draft.',
        },
      ]);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to save draft',
      );
    } finally {
      setSaving(false);
    }
  };

  const openAgent = (idOrName: string) => {
    navigate(`/agents/${encodeURIComponent(idOrName)}`);
  };

  const onDropDs = (name: string) => {
    setAttachedRefs((prev) => (prev.includes(name) ? prev : [...prev, name]));
    if (!chatInput.trim()) {
      setChatInput(`Associate and use ${name} in the system prompt.`);
    }
  };

  const fleetDiffLines = useMemo(() => {
    if (!pending?.fleet) return [];
    const current = new Set(fleetNames);
    const next = new Set(pending.fleet);
    const removed = fleetNames.filter((n) => !next.has(n));
    const added = pending.fleet.filter((n) => !current.has(n));
    return [
      ...removed.map((n) => `− ${n}`),
      ...added.map((n) => `+ ${n}`),
    ];
  }, [pending, fleetNames]);

  const availableToAssociate = useMemo(() => {
    const linked = new Set(dataSources.map((d) => d.dataSourceId));
    return (orgDataSources || []).filter((d) => !linked.has(d.dataSourceId));
  }, [orgDataSources, dataSources]);

  const modelOptions = useMemo(() => {
    const set = new Set([
      ...MODEL_OPTIONS,
      config.model,
      ...config.recommendedModels,
    ]);
    return [...set].filter(Boolean);
  }, [config.model, config.recommendedModels]);

  return (
    <Layout>
      <EditorPane>
        {needsVersion && (
          <DraftBanner>
            <span>
              Create a new version to edit Live Configuration or apply Agent
              Builder changes.
            </span>
            {onCreateVersion && (
              <Button variant="primary" size="sm" onClick={onCreateVersion}>
                Create New Version
              </Button>
            )}
          </DraftBanner>
        )}

        <ChatShell>
          <ChatHeader>
            <ChatTitle>Agent Builder</ChatTitle>
            <ChatSubtitle>
              Describe changes in natural language. Drag a data source into the
              dropzone — AI proposes diffs you Accept or Discard. Never asks for
              API secrets.
            </ChatSubtitle>
          </ChatHeader>
          <MessageList>
            {messages.map((m) => (
              <Bubble key={m.id} $role={m.role}>
                {m.content}
              </Bubble>
            ))}
            {showPending && (
              <PendingBubble role="status">Thinking…</PendingBubble>
            )}
            <div ref={endRef} />
          </MessageList>
          <Dropzone
            $over={dropOver}
            data-testid="builder-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDropOver(true);
            }}
            onDragLeave={() => setDropOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDropOver(false);
              const name = e.dataTransfer.getData('text/plain');
              if (name) onDropDs(name);
            }}
          >
            <DropLabel $over={dropOver}>
              {dropOver
                ? 'Drop data source here'
                : 'Agent Builder dropzone'}
            </DropLabel>
            {attachedRefs.length > 0 && (
              <AttachedRefs>
                {attachedRefs.map((name) => (
                  <AttachedRef key={name}>
                    $DataSource:{name}
                    <ChipRemove
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() =>
                        setAttachedRefs((prev) =>
                          prev.filter((n) => n !== name),
                        )
                      }
                    >
                      ×
                    </ChipRemove>
                  </AttachedRef>
                ))}
              </AttachedRefs>
            )}
            <ChatInputRow
              onSubmit={(e) => {
                e.preventDefault();
                void sendQuery(chatInput);
              }}
            >
              <ChatInput
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Drop a data source here or ask to configure…"
                disabled={showPending || needsVersion}
                rows={1}
              />
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={
                  (!chatInput.trim() && !attachedRefs.length) ||
                  showPending ||
                  needsVersion
                }
              >
                Send
              </Button>
            </ChatInputRow>
          </Dropzone>
        </ChatShell>

        {displayChips.length > 0 && (
          <ChipRow>
            {displayChips.map((chip) => (
              <Chip
                key={chip}
                type="button"
                disabled={showPending || needsVersion}
                onClick={() => handleChipClick(chip)}
              >
                {chip}
              </Chip>
            ))}
          </ChipRow>
        )}

        {pending && (
          <DiffCard>
            <strong>Proposed configuration change</strong>
            <div style={{ marginTop: '0.5rem' }}>
              {pending.model != null && pending.model !== config.model && (
                <div>
                  Model: {config.model} → {pending.model}
                </div>
              )}
              {pending.temperature != null &&
                pending.temperature !== config.temperature && (
                  <div>
                    Temperature: {config.temperature} → {pending.temperature}
                  </div>
                )}
              {pending.systemPrompt != null &&
                pending.systemPrompt !== config.systemPrompt && (
                  <div>
                    System prompt updated
                    {pending.systemPrompt.includes('$DataSource:')
                      ? ' (includes $DataSource: directive)'
                      : ''}
                  </div>
                )}
              {pending.recommendedModels && (
                <div>
                  Recommended: {pending.recommendedModels.join(', ') || '—'}
                </div>
              )}
              {pending.dataSources && (
                <div>
                  Data sources: {pending.dataSources.join(', ') || '—'}
                </div>
              )}
              {fleetDiffLines.length > 0 && (
                <div
                  style={{
                    marginTop: '0.35rem',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {fleetDiffLines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  <div>({pending.fleet?.length ?? 0} specialists)</div>
                </div>
              )}
            </div>
            <DiffActions>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAccept}
                disabled={saving || !editsEnabled}
              >
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscard}
                disabled={saving}
              >
                Discard
              </Button>
            </DiffActions>
          </DiffCard>
        )}

        {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

        {(management || fleetNames.length > 0) && (
          <ConfigureFleetPanel
            fleet={{
              agents: fleetNames.map((name) => {
                const existing = agent.fleet?.agents.find(
                  (m) => m.agentName === name,
                );
                return (
                  existing || {
                    agentName: name,
                    displayName: name,
                  }
                );
              }),
              orchestrationMode: agent.fleet?.orchestrationMode,
              orchestrationLabel: agent.fleet?.orchestrationLabel,
            }}
            catalog={catalog.filter((a) => a.id !== agent.id)}
            readOnly={!editsEnabled}
            onChange={handleFleetChange}
            onOpenAgent={openAgent}
          />
        )}
        {agent.membership && !management && (
          <ConfigureMembershipPanel
            membership={agent.membership}
            onOpenParent={openAgent}
          />
        )}
      </EditorPane>

      <PreviewCard padding={20} elevation="sm">
        <Toolbar>
          <div>
            <Text variant="h4" style={{ margin: 0 }}>
              Live Configuration
            </Text>
            <Helper>
              Edit fields directly or via Agent Builder.
              {dirty ? ' Unsaved changes.' : ''}
            </Helper>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardEdits}
              disabled={!dirty || saving || !editsEnabled}
            >
              Discard edits
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveDraft}
              disabled={!dirty || saving || !editsEnabled}
            >
              {saving ? 'Saving…' : 'Save to Draft'}
            </Button>
          </div>
        </Toolbar>

        {(management || preview.fleet.length > 0) && (
          <div>
            <BlockHeader>Fleet & orchestration</BlockHeader>
            <FieldRow>
              <FieldName>mode</FieldName>
              <span style={{ fontWeight: 500 }}>
                {preview.orchestrationMode || '—'}
              </span>
            </FieldRow>
            <FieldRow>
              <FieldName>members</FieldName>
              <span style={{ fontWeight: 500, textAlign: 'right' }}>
                {preview.fleet.length ? preview.fleet.join(', ') : 'None'}
              </span>
            </FieldRow>
          </div>
        )}

        <div>
          <BlockHeader>Model Settings</BlockHeader>
          <FieldRow>
            <FieldName>default_model</FieldName>
            <ConfigSelect
              value={config.model}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({ ...c, model: e.target.value }))
              }
              aria-label="Default model"
            >
              {modelOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </ConfigSelect>
          </FieldRow>
          <FieldRow>
            <FieldName>recommended_models</FieldName>
            <FieldStack>
              <ChipRow>
                {config.recommendedModels.map((m) => (
                  <RemovableChip key={m}>
                    {m}
                    <ChipRemove
                      type="button"
                      aria-label={`Remove ${m}`}
                      disabled={!editsEnabled}
                      onClick={() =>
                        setConfig((c) => ({
                          ...c,
                          recommendedModels: c.recommendedModels.filter(
                            (x) => x !== m,
                          ),
                        }))
                      }
                    >
                      ×
                    </ChipRemove>
                  </RemovableChip>
                ))}
                <ConfigSelect
                  value=""
                  disabled={!editsEnabled}
                  aria-label="Add recommended model"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    setConfig((c) =>
                      c.recommendedModels.includes(value)
                        ? c
                        : {
                            ...c,
                            recommendedModels: [
                              ...c.recommendedModels,
                              value,
                            ],
                          },
                    );
                    e.target.value = '';
                  }}
                  style={{ maxWidth: '9rem' }}
                >
                  <option value="">+ Add…</option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </ConfigSelect>
              </ChipRow>
            </FieldStack>
          </FieldRow>
          <Helper>
            Playground and API may pass <code>model</code> to override for a
            single exchange. Recommended list feeds future auto mode.
          </Helper>
          <FieldRow>
            <FieldName>temperature</FieldName>
            <ConfigInput
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={config.temperature}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  temperature: Number(e.target.value),
                }))
              }
              aria-label="Temperature"
            />
          </FieldRow>
          <FieldRow>
            <FieldName>max_tokens</FieldName>
            <ConfigInput
              type="number"
              min={1}
              step={1}
              value={config.maxTokens}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  maxTokens: Number(e.target.value),
                }))
              }
              aria-label="Max tokens"
            />
          </FieldRow>
          <FieldRow>
            <FieldName>response_mode</FieldName>
            <ConfigSelect
              value={config.responseMode}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({ ...c, responseMode: e.target.value }))
              }
              aria-label="Response mode"
            >
              <option value="async">async</option>
              <option value="sync">sync</option>
            </ConfigSelect>
          </FieldRow>
        </div>

        <div>
          <BlockHeader>System prompt</BlockHeader>
          <ConfigTextarea
            value={config.systemPrompt}
            disabled={!editsEnabled}
            onChange={(e) =>
              setConfig((c) => ({ ...c, systemPrompt: e.target.value }))
            }
            aria-label="System prompt"
            placeholder="Supports $DataSource:Name directives"
          />
        </div>

        <div>
          <BlockHeader>Data sources</BlockHeader>
          {dataSources.length === 0 ? (
            <Helper>No data sources associated yet.</Helper>
          ) : (
            dataSources.map((ds) => (
              <DsRow key={ds.dataSourceId}>
                <span>
                  {ds.name}{' '}
                  <Badge
                    variant={
                      (DS_TYPE_BADGE_VARIANT[ds.dsType as DsType] as
                        | 'info'
                        | 'success'
                        | 'warning'
                        | 'neutral') || 'neutral'
                    }
                    size="sm"
                  >
                    {dsTypeLabel(ds.dsType)}
                  </Badge>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!editsEnabled}
                  onClick={() => setRemoveConfirm(ds)}
                >
                  Remove
                </Button>
              </DsRow>
            ))
          )}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginTop: '0.75rem',
            }}
          >
            <Button
              variant="outline"
              size="sm"
              disabled={!editsEnabled}
              onClick={() => {
                setPickerSelected(dataSources.map((d) => d.dataSourceId));
                setAssociateOpen(true);
              }}
            >
              + Associate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(
                  `/data-sources?create=1&returnTo=${encodeURIComponent(`/agents/${agent.id}`)}&tab=configure`,
                )
              }
            >
              + Create data source
            </Button>
          </div>
          {dataSources.length > 0 && (
            <DsDragTray data-testid="ds-drag-tray">
              <DsDragLabel>
                Drag into Agent Builder
                <span
                  style={{
                    marginLeft: 'auto',
                    fontWeight: 400,
                    color: 'var(--text-muted)',
                    fontSize: '0.6875rem',
                  }}
                >
                  Grip · drag chip → dropzone
                </span>
              </DsDragLabel>
              <ChipRow>
                {dataSources.map((ds) => (
                  <DsChip
                    key={ds.dataSourceId}
                    draggable={editsEnabled}
                    title="Drag into Agent Builder"
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', ds.name);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                  >
                    <Grip aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </Grip>
                    {ds.name}
                  </DsChip>
                ))}
              </ChipRow>
            </DsDragTray>
          )}
        </div>

        <ComingSoon>
          <BlockHeader>Actions</BlockHeader>
          <Helper style={{ margin: 0 }}>
            Integrations coming next — same associate + drag-into-builder
            pattern as data sources.
          </Helper>
        </ComingSoon>

        {pending && (
          <Badge variant="warning" size="sm">
            Pending changes (Accept to apply to Draft)
          </Badge>
        )}
        <Helper>
          Empty fleet is warned on Publish. Changes stay on Draft until you
          publish.
        </Helper>
      </PreviewCard>

      <Modal
        isOpen={associateOpen}
        onClose={() => setAssociateOpen(false)}
        title="Associate data sources"
      >
        <Text variant="body1" style={{ margin: 0, fontSize: '0.875rem' }}>
          Pick from your organisation catalog. Credentials stay on the Data
          Sources wizard — never paste secrets here.
        </Text>
        <PickerList>
          {(orgDataSources || []).map((ds) => (
            <PickerRow key={ds.dataSourceId}>
              <input
                type="checkbox"
                checked={pickerSelected.includes(ds.dataSourceId)}
                onChange={(e) => {
                  setPickerSelected((prev) =>
                    e.target.checked
                      ? [...prev, ds.dataSourceId]
                      : prev.filter((id) => id !== ds.dataSourceId),
                  );
                }}
              />
              <span>
                {ds.name} · {dsTypeLabel(ds.dsType)}
              </span>
            </PickerRow>
          ))}
          {(orgDataSources || []).length === 0 && (
            <Helper>No data sources in this organisation yet.</Helper>
          )}
        </PickerList>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setAssociateOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!editsEnabled || saving}
            onClick={async () => {
              setSaving(true);
              setActionError(null);
              try {
                await persistDataSources(pickerSelected);
                const next = (orgDataSources || [])
                  .filter((d) => pickerSelected.includes(d.dataSourceId))
                  .map((d) => ({
                    dataSourceId: d.dataSourceId,
                    name: d.name,
                    dsType: d.dsType,
                    status: d.status,
                  }));
                setDataSources(next);
                setSavedDataSourceIds(pickerSelected);
                setAssociateOpen(false);
              } catch (err) {
                setActionError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to update data sources',
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            Save associations
          </Button>
        </div>
        {availableToAssociate.length === 0 &&
          (orgDataSources || []).length > 0 && (
            <Helper>All org data sources are already linked.</Helper>
          )}
      </Modal>

      <Modal
        isOpen={Boolean(removeConfirm)}
        onClose={() => setRemoveConfirm(null)}
        title="Remove data source?"
      >
        <Text variant="body1" style={{ margin: '0 0 1rem' }}>
          Unlink <strong>{removeConfirm?.name}</strong> from this agent? You can
          re-associate later.
        </Text>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setRemoveConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving}
            onClick={async () => {
              if (!removeConfirm) return;
              setSaving(true);
              setActionError(null);
              try {
                const nextIds = dataSources
                  .filter((d) => d.dataSourceId !== removeConfirm.dataSourceId)
                  .map((d) => d.dataSourceId);
                await persistDataSources(nextIds);
                const next = dataSources.filter(
                  (d) => d.dataSourceId !== removeConfirm.dataSourceId,
                );
                setDataSources(next);
                setSavedDataSourceIds(nextIds);
                setRemoveConfirm(null);
              } catch (err) {
                setActionError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to remove data source',
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </Layout>
  );
};

export default ConfigureTab;
export { parseBuilderResponse, toProposed, configsDiffer };
